import { Injectable, Inject } from '@angular/core';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { UserSettingsService } from 'catalean-authentication';
import { Resource, Collection, Product } from 'catalean-models';
import { ANALYTICS_CONFIG, AnalyticsConfig } from './analytics.config';
import {
  AnalyticsEventType,
  ContentType,
  ContentViewEvent,
  ContentDownloadEvent,
  ContentShareEvent,
  CollectionEvent,
  UserLoginEvent,
  SharedLinkOpenEvent
} from './analytics.models';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  
  constructor(
    @Inject(ANALYTICS_CONFIG) private config: AnalyticsConfig,
    private gtmService: GoogleTagManagerService,
    private userSettings: UserSettingsService
  ) {}

  /**
   * Metodo generico per inviare eventi al dataLayer
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pushEvent(eventName: string, eventData: any): void {
    if (!this.config.enabled) {
      return;
    }

    const enrichedData = {
      event: eventName,
      ...eventData,
      timestamp: Date.now(),
      environment: this.config.environment
    };

    // Usa il servizio GTM della libreria per pushare eventi
    this.gtmService.pushTag(enrichedData);

    if (this.config.debug) {
      console.log('[Analytics] Event pushed:', eventName, enrichedData);
    }
  }

  /**
   * Ottiene i dati base dell'utente corrente
   */
  private getBaseUserData() {
    const user = this.userSettings.getAuthenticatedUserData();
    console.log(user?.Id);
    
    return {
      user_id: user?.Id || 'unknown',
      user_group: user?.StructureUUID || 'unknown'
    };
  }

  /**
   * Determina il tipo di contenuto dall'estensione/format
   */
  private getContentType(resource: Resource): ContentType {
    const ext = resource.format?.toLowerCase() || '';
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) {
      return ContentType.IMAGE;
    }
    if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'wmv', 'flv', '3gp'].includes(ext)) {
      return ContentType.VIDEO;
    }
    return ContentType.DOCUMENT;
  }

  /**
   * TRACCIAMENTO VISUALIZZAZIONE CONTENUTO
   */
  trackContentView(resource: Resource, product?: Product): void {
    const eventData: ContentViewEvent = {
      ...this.getBaseUserData(),
      content_id: resource.uuid,
      content_name: resource.name || 'Unknown',
      content_url: resource.url,
      content_type: this.getContentType(resource),
      product_id: product?.uuid,
      product_name: product?.name,
      file_extension: resource.format,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.CONTENT_VIEW, eventData);
  }

  /**
   * TRACCIAMENTO DOWNLOAD CONTENUTO
   */
  trackContentDownload(
    resource: Resource, 
    product?: Product, 
    downloadMethod: 'direct' | 'browser' = 'direct'
  ): void {
    const eventData: ContentDownloadEvent = {
      ...this.getBaseUserData(),
      content_id: resource.uuid,
      content_name: resource.name || 'Unknown',
      content_type: this.getContentType(resource),
      content_url: resource.url,
      product_id: product?.uuid,
      product_name: product?.name,
      file_extension: resource.format,
      file_size: resource.bytes ? parseInt(resource.bytes, 10) : undefined,
      download_method: downloadMethod,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.CONTENT_DOWNLOAD, eventData);
  }

  /**
   * TRACCIAMENTO CONDIVISIONE CONTENUTO
   */
  trackContentShare(
    resource: Resource, 
    shareLink: string,
    shareType: 'collection' | 'asset' = 'asset',
    product?: Product,
    collection?: Collection
  ): void {
    const eventData: ContentShareEvent = {
      ...this.getBaseUserData(),
      content_id: resource.uuid,
      content_name: resource.name || 'Unknown',
      content_url: resource.url,
      content_type: this.getContentType(resource),
      product_id: product?.uuid,
      product_name: product?.name,
      collection_id: collection?.uuid,
      collection_name: collection?.name,
      file_extension: resource.format,
      share_link: shareLink,
      share_type: shareType,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.CONTENT_SHARE, eventData);
  }

  /**
   * TRACCIAMENTO VISUALIZZAZIONE COLLEZIONE
   */
  trackCollectionView(collection: Collection): void {
    const eventData: CollectionEvent = {
      ...this.getBaseUserData(),
      collection_id: collection.uuid,
      collection_name: collection.name,
      is_public: collection.isPublic,
      items_count: collection.assetsRef?.length || 0,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.COLLECTION_VIEW, eventData);
  }

  /**
   * TRACCIAMENTO CONDIVISIONE COLLEZIONE
   */
  trackCollectionShare(collection: Collection, shareLink: string): void {
    const eventData = {
      ...this.getBaseUserData(),
      collection_id: collection.uuid,
      collection_name: collection.name,
      is_public: collection.isPublic,
      items_count: collection.assetsRef?.length || 0,
      share_link: shareLink,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.COLLECTION_SHARE, eventData);
  }

  /**
   * TRACCIAMENTO CREAZIONE COLLEZIONE
   */
  trackCollectionCreate(collection: Collection): void {
    const eventData: CollectionEvent = {
      ...this.getBaseUserData(),
      collection_id: collection.uuid,
      collection_name: collection.name,
      is_public: collection.isPublic,
      items_count: 0,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.COLLECTION_CREATE, eventData);
  }

  /**
   * TRACCIAMENTO ACCESSO UTENTE
   */
  trackUserLogin(platform: 'ios' | 'android' | 'web' = 'web'): void {
    const eventData: UserLoginEvent = {
      ...this.getBaseUserData(),
      login_time: Date.now(),
      device_type: this.getDeviceType(),
      platform: platform,
      timestamp: Date.now()
    };

    this.pushEvent(AnalyticsEventType.USER_LOGIN, eventData);

    // Imposta anche le user properties per GA4
    this.setUserProperties({
      user_id: eventData.user_id,
      user_group: eventData.user_group,
    });
  }

  /**
   * TRACCIAMENTO APERTURA LINK CONDIVISO
   */
  trackSharedLinkOpen(
    contentId: string,
    contentType: 'asset' | 'collection',
    contentName: string = 'unknown',
    isAuthenticated: boolean = false
  ): void {
    const eventData: SharedLinkOpenEvent = {
      viewer_type: isAuthenticated ? 'authenticated' : 'guest',
      content_id: contentId,
      content_type: contentType,
      content_name: contentName,
      timestamp: Date.now(),
      referrer: document.referrer
    };

    this.pushEvent(AnalyticsEventType.SHARED_LINK_OPEN, eventData);
  }

  /**
   * Imposta le user properties per GA4
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setUserProperties(properties: Record<string, any>): void {
    if (!this.config.enabled) return;

    this.gtmService.pushTag({
      event: 'set_user_properties',
      user_properties: properties
    });
  }

  /**
   * Determina il tipo di dispositivo
   */
  private getDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'electron' {
    const ua = navigator.userAgent.toLowerCase();
    
    if (ua.includes('electron')) return 'electron';
    if (ua.includes('tablet') || ua.includes('ipad')) return 'tablet';
    if (ua.includes('mobile') || ua.includes('android')) return 'mobile';
    return 'desktop';
  }

  /**
   * Abilita/disabilita debug mode
   */
  setDebugMode(enabled: boolean): void {
    this.config.debug = enabled;
  }
}